// router.js — History-API-Router.
//   const r = createRouter({ base: '/admin', routes: [{ path: '/akquise/leads/:id', load: () => import('…') }], onChange });
//   r.go('/akquise/leads/5'); r.back(); r.current.value → { route, params, query, path }
// Reine Hilfsfunktionen (compile/match/parseQuery/stringifyQuery) sind exportiert und in Node testbar.
import { signal } from './store.js';

export function compile(path) {
  const keys = [];
  const src = path.replace(/\/+$/, '') || '/';
  const re = src === '/' ? '^/?$' : '^' + src.split('/').map((seg) => {
    if (!seg) return '';
    if (seg === '*') { keys.push('rest'); return '(?:/(.*))?'; }
    if (seg.startsWith(':')) {
      const opt = seg.endsWith('?');
      keys.push(seg.slice(1, opt ? -1 : undefined));
      return opt ? '(?:/([^/]+))?' : '/([^/]+)';
    }
    return '/' + seg.replace(/[.+^${}()|[\]\\]/g, '\\$&');
  }).join('') + '/?$';
  return { keys, re: new RegExp(re) };
}

export function match(routes, pathname) {
  for (const route of routes) {
    route._c = route._c || compile(route.path);
    const m = route._c.re.exec(pathname);
    if (!m) continue;
    const params = {};
    route._c.keys.forEach((k, i) => { if (m[i + 1] !== undefined) params[k] = decodeURIComponent(m[i + 1]); });
    return { route, params };
  }
  return null;
}

export function parseQuery(search) {
  const out = {};
  new URLSearchParams(search || '').forEach((v, k) => { out[k] = v; });
  return out;
}

export function stringifyQuery(q) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q || {})) if (v != null && v !== '' && v !== false) p.set(k, String(v));
  const s = p.toString();
  return s ? '?' + s : '';
}

/** Basis abschneiden: stripBase('/next/admin/x', '/next/admin') → '/x'. Gibt null zurück, wenn außerhalb. */
export function stripBase(pathname, base) {
  const b = (base || '').replace(/\/+$/, '');
  if (!b) return pathname || '/';
  if (pathname === b) return '/';
  if (pathname.startsWith(b + '/')) return pathname.slice(b.length);
  return null;
}

export function createRouter({ base = '', routes = [], onChange, notFound } = {}) {
  const current = signal(null);
  let token = 0;

  async function resolve() {
    const my = ++token;
    const inner = stripBase(location.pathname, base);
    const query = parseQuery(location.search);
    const hit = inner == null ? null : match(routes, inner);
    const state = hit
      ? { route: hit.route, params: hit.params, query, path: inner, hash: location.hash }
      : { route: notFound || null, params: {}, query, path: inner, hash: location.hash, notFound: true };
    if (state.route && state.route.load && !state.route.module) {
      try { state.route.module = await state.route.load(); } catch (e) { state.error = e; }
      if (my !== token) return; // überholt
    }
    current.value = state;
    if (onChange) onChange(state);
  }

  function href(path, query) { return (base.replace(/\/+$/, '') + (path.startsWith('/') ? path : '/' + path)) + stringifyQuery(query); }

  function go(path, { query, replace = false, state = null } = {}) {
    const url = href(path, query);
    if (url === location.pathname + location.search) return resolve();
    history[replace ? 'replaceState' : 'pushState'](state, '', url);
    return resolve();
  }

  /** Nur Query ändern (z. B. Filter, Dialog `d`), Pfad bleibt. */
  function setQuery(patch, { replace = true } = {}) {
    const q = { ...parseQuery(location.search), ...patch };
    history[replace ? 'replaceState' : 'pushState'](history.state, '', location.pathname + stringifyQuery(q));
    return resolve();
  }

  function onClick(e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a || a.target === '_blank' || a.hasAttribute('download') || a.dataset.native != null) return;
    const url = new URL(a.href, location.href);
    if (url.origin !== location.origin) return;
    const inner = stripBase(url.pathname, base);
    if (inner == null || !match(routes, inner)) return;
    e.preventDefault();
    history.pushState(null, '', url.pathname + url.search + url.hash);
    resolve();
  }

  function start() {
    addEventListener('popstate', resolve);
    document.addEventListener('click', onClick);
    return resolve();
  }
  function stop() { removeEventListener('popstate', resolve); document.removeEventListener('click', onClick); }

  return { current, go, setQuery, href, back: () => history.back(), start, stop, resolve };
}
